import { useState } from "react";
import "./RequestTimeline.css";

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden>
    <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden>
    <circle cx="6" cy="6" r="4.5" stroke="#fff" strokeWidth="1.4"/>
    <polyline points="6,3.5 6,6 8,7.5" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const STEPS = {
  TRAVELER: [
    {
      label: "Details Submitted",
      desc:  "You're registered! We've received your flight details.",
    },
    {
      label: "Verification Ongoing",
      desc:  "Our ops team is verifying your details — we may reach out if anything is needed.",
    },
    {
      label: "Searching for Match",
      desc:  "Looking for a sender whose route matches your flight.",
    },
    {
      label: "Match Confirmed",
      desc:  "Great news! A sender has been matched to your route.",
    },
    {
      label: "In Progress",
      desc:  "You have the parcel — please ensure safe delivery at the destination.",
    },
    {
      label: "Completed",
      desc:  "Delivery done. Thank you for being a TurantX traveler!",
    },
  ],
  SENDER: [
    {
      label: "Details Submitted",
      desc:  "You're registered! We've received your delivery request.",
    },
    {
      label: "Verification Ongoing",
      desc:  "Our ops team is reviewing your request — we may reach out if anything is needed.",
    },
    {
      label: "Searching for Match",
      desc:  "Finding the best traveler for your route.",
    },
    {
      label: "Match Confirmed",
      desc:  "A traveler has been matched for your delivery.",
    },
    {
      label: "In Progress",
      desc:  "Your traveler is carrying the parcel — delivery underway.",
    },
    {
      label: "Completed",
      desc:  "Your document has been delivered successfully!",
    },
  ],
};

/**
 * Maps an order status to this timeline's 1-based step (6 visible steps:
 * Details → Verification → Searching → Match Confirmed → In Progress → Completed).
 * NOTE: this is a DIFFERENT scale from getStep()/StepIndicator (total 8) —
 * do not feed getStep() here or IN_PROGRESS overshoots and every step reads
 * as done. COMPLETED returns 7 so all six steps render as finished.
 */
export function timelineStepFor(status) {
  switch (status) {
    case "COMPLETED":   return 7; // past the last step → all six done
    case "IN_PROGRESS": return 5;
    case "MATCHED":     return 4;
    case "SEARCHING":   return 3;
    case "NO_MATCH":    return 3;
    default:            return 2; // NEW_ORDER etc. — verification ongoing
  }
}

// Which status stamp lights up each of the 6 rows (index-aligned to STEPS).
// Row 0 (Details) uses createdAt; row 1 (Verification) has no discrete status.
const STEP_STAMP = [null, null, "SEARCHING", "MATCHED", "IN_PROGRESS", "COMPLETED"];

function fmtStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
  });
}

export default function RequestTimeline({ currentStep = 1, userType = "SENDER", stamps = {}, createdAt = null }) {
  const [expanded, setExpanded] = useState(false);

  const steps    = STEPS[userType] || STEPS.SENDER;
  const active   = steps[Math.min(currentStep - 1, steps.length - 1)];
  const doneCount = Math.min(currentStep - 1, steps.length);

  const stampFor = (i) => fmtStamp(i === 0 ? createdAt : (STEP_STAMP[i] ? stamps?.[STEP_STAMP[i]] : null));

  return (
    <div className="rtl-card">
      <button
        className="rtl-header"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <div className="rtl-header-left">
          <div className="rtl-dot active"><ClockIcon /></div>
          <div>
            <div className="rtl-active-label">{active.label}</div>
            <div className="rtl-count">{doneCount} of {steps.length} steps done</div>
          </div>
        </div>
        <svg className={`rtl-chevron${expanded ? " open" : ""}`} viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      <div className={`rtl-collapse${expanded ? " open" : ""}`}>
        <div className="rtl-inner">
          <div className="rtl-timeline">
            {steps.map((step, i) => {
              const done   = i + 1 < currentStep;
              const isActive = i + 1 === currentStep;
              const last   = i === steps.length - 1;
              const when   = (done || isActive) ? stampFor(i) : "";
              return (
                <div key={i} className="rtl-row">
                  <div className="rtl-node">
                    <div className={`rtl-dot${done ? " done" : isActive ? " active" : " pending"}`}>
                      {done ? <CheckIcon /> : isActive ? <ClockIcon /> : null}
                    </div>
                    {!last && <div className={`rtl-line${done ? " done" : ""}`} />}
                  </div>
                  <div className="rtl-content">
                    <span className={`rtl-label${done ? " done" : isActive ? " active" : " pending"}`}>
                      {step.label}
                      {when && <span className="rtl-time">{when}</span>}
                    </span>
                    {isActive && <span className="rtl-desc">{step.desc}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
